import { useState } from "react";
import { SearchInput } from "@/core/components/search-input";
import { Card } from "@/core/components/ui/card";
import { Stack } from "@/core/components/ui/stack";
import {
  Tabs,
  TabsContent,
  TabsListWithIndicator,
  TabsTrigger,
} from "@/core/components/ui/tabs/tabs";
import { Typography } from "@/core/components/ui/typography";
import { ICM_TAB, IcmTab } from "@/icm/constants";
import type { EnrichedIcmStatement, IcmJob } from "@/icm/types";
import { IcmJobBreakdownTable } from "./job-breakdown-table";
import { IcmTechnicianBreakdownTable } from "./technician-breakdown-table";

interface IcmPageBreakdownCardProps {
  periodStart?: string;
  statements: EnrichedIcmStatement[];
  jobs: IcmJob[];
  onSelectJob: (job: IcmJob | null) => void;
}

export const IcmPageBreakdownCard = ({
  periodStart,
  statements,
  jobs,
  onSelectJob,
}: IcmPageBreakdownCardProps) => {
  const [activeTab, setActiveTab] = useState<IcmTab>(ICM_TAB.technicians);
  const [tabSearch, setTabSearch] = useState("");

  return (
    <Card className="w-full overflow-hidden">
      <Tabs
        value={activeTab}
        onValueChange={(tab) => {
          setActiveTab(tab as IcmTab);
          setTabSearch("");
        }}
        className="relative flex flex-1 flex-col overflow-hidden"
      >
        <Stack
          direction="row"
          align="center"
          justify="between"
          className="w-full flex-grow-0 border-b"
        >
          <TabsListWithIndicator hideBottomBorder className="justify-start">
            <TabsTrigger value={ICM_TAB.technicians}>
              Technician Breakdown
            </TabsTrigger>
            <TabsTrigger value={ICM_TAB.jobs}>Job Breakdown</TabsTrigger>
          </TabsListWithIndicator>
          <SearchInput
            placeholder="Search..."
            value={tabSearch}
            onChange={setTabSearch}
            className="mr-4"
          />
        </Stack>
        <TabsContent value={ICM_TAB.technicians}>
          <IcmTechnicianBreakdownTable
            periodStart={periodStart}
            statements={statements}
            searchValue={tabSearch}
            setSearchValue={setTabSearch}
          />
        </TabsContent>
        <TabsContent value={ICM_TAB.jobs}>
          {jobs.length > 0 ? (
            <IcmJobBreakdownTable
              jobs={jobs}
              searchValue={tabSearch}
              setSearchValue={setTabSearch}
              onRowClick={onSelectJob}
            />
          ) : (
            <Typography variant="bodyW2" textColor="secondary">
              No job data available for this period.
            </Typography>
          )}
        </TabsContent>
      </Tabs>
    </Card>
  );
};
